import { useRef, useState } from 'react'
import calendarIcon from '../../../../assets/img/calendar-icon.svg'
import locationIcon from '../../../../assets/img/location-icon.svg'
import {
  BuildingIcon,
  UsersIcon,
  MoreVerticalIcon,
  TrophyIcon,
  CheckIcon,
  EditIcon,
  TrashIcon
} from '../../../../assets/svg'
import { useOnClickOutside } from '../../../../hooks'


const STATUSES = {
  draft:       { label: 'Черновик',    bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  planned:     { label: 'Планируется', bg: '#ede9fe', text: '#6d28d9', border: '#a78bfa' },
  accepting:   { label: 'Приём заявок', bg: '#e0f2fe', text: '#0369a1', border: '#18a1ed' },
  closed:      { label: 'Приём закрыт', bg: '#fee2e2', text: '#991b1b', border: '#f87171' },
  in_progress: { label: 'Проводится',  bg: '#fef3c7', text: '#b45309', border: '#fbbf24' },
  finished:    { label: 'Завершено',   bg: '#d1fada', text: '#228b51', border: '#34d383' },
  cancelled:   { label: 'Отменено',    bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' }
}

const FALLBACK_STATUS = { label: '—', bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' }

const EventCard = ({ title, status, dates, place, responsible, commission, hasResults, onEdit, onDelete }) => {
  const s = STATUSES[status] || FALLBACK_STATUS
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const menuRef = useRef(null)

  useOnClickOutside(menuRef, () => setMenuOpen(false))

  if (confirmingDelete) {
    return (
      <article
        className="flex w-full flex-col items-center justify-center gap-[1.25rem] rounded-[20px] border-[1px] border-solid border-[#e5e7eb] bg-white p-[2rem] text-center"
      >
        <div className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-[#fee2e2]">
          <TrashIcon />
        </div>
        <h3 className="m-0 text-[1.25rem] font-semibold text-[#111827]">
          Удалить мероприятие?
        </h3>
        <p className="m-0 text-[0.938rem] text-[#6b7280]">
          Это действие нельзя будет отменить
        </p>
        <div className="flex w-full items-center justify-center gap-[0.625rem]">
          <button
            type="button"
            onClick={() => setConfirmingDelete(false)}
            className="rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1.5rem] py-[0.75rem] text-[0.938rem] font-medium text-[#374151] hover:bg-[#f9fafb]"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => { setConfirmingDelete(false); onDelete?.() }}
            className="rounded-[10px] bg-[#ef4444] px-[1.5rem] py-[0.75rem] text-[0.938rem] font-medium text-white hover:bg-[#dc2626]"
          >
            Удалить
          </button>
        </div>
      </article>
    )
  }

  return (
    <article
      className="flex w-full flex-col gap-[1rem] rounded-[20px] border-[1px] border-solid bg-white p-[1.25rem] sm:gap-[1.25rem] sm:p-[1.5rem] lg:p-[2rem]"
      style={{ borderColor: s.border }}
    >
      <div className="flex flex-wrap items-start justify-between gap-[0.625rem]">
        <h3 className="m-0 min-w-0 flex-1 break-words text-[1.125rem] font-semibold leading-[1.25] text-[#111827] sm:text-[1.25rem]">
          {title}
        </h3>
        <div className="flex shrink-0 items-center gap-[0.5rem]">
          <span
            className="flex items-center gap-[0.312rem] whitespace-nowrap rounded-[50px] px-[0.75rem] py-[0.375rem] text-[0.812rem] font-medium sm:px-[1rem] sm:py-[0.5rem] sm:text-[1rem]"
            style={{ backgroundColor: s.bg, color: s.text }}
          >
            <CheckIcon color={s.text} />
            {s.label}
          </span>
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-[2.25rem] w-[2.25rem] shrink-0 items-center justify-center rounded-[8px] hover:bg-[#f3f4f6]"
            >
              <MoreVerticalIcon />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-[2.5rem] z-10 flex w-[13.75rem] flex-col gap-[0.25rem] rounded-[12px] border border-solid border-[#e5e7eb] bg-white p-[0.5rem] shadow-[0px_8px_24px_rgba(0,0,0,0.08)]">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onEdit?.() }}
                  className="flex items-center gap-[0.625rem] rounded-[8px] px-[0.875rem] py-[0.625rem] text-left text-[0.938rem] text-[#374151] hover:bg-[#f3f4f6]"
                >
                  <EditIcon />
                  Редактировать
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setConfirmingDelete(true) }}
                  className="flex items-center gap-[0.625rem] rounded-[8px] px-[0.875rem] py-[0.625rem] text-left text-[0.938rem] text-[#ef4444] hover:bg-[#fee2e2]"
                >
                  <TrashIcon />
                  Удалить
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-[0.375rem] text-[0.875rem] text-[#6b7280] sm:text-[1rem]">
        <div className="flex items-center gap-[0.625rem]">
          <img src={calendarIcon} alt="" className="h-[1.25rem] w-[1.25rem] shrink-0" />
          <span className="min-w-0 break-words">{dates}</span>
        </div>
        <div className="flex items-center gap-[0.625rem]">
          <img src={locationIcon} alt="" className="h-[1.25rem] w-[1.25rem] shrink-0" />
          <span className="min-w-0 break-words">{place}</span>
        </div>
      </div>

      <div className="h-px w-full bg-[#e5e7eb]" />

      <div className="flex items-start gap-[0.625rem]">
        <span className="shrink-0"><BuildingIcon /></span>
        <div className="flex min-w-0 flex-1 flex-col gap-[0.312rem] text-[0.875rem] sm:text-[1rem]">
          <span className="text-[#9ca3af]">Ответственный</span>
          <span className="break-words text-[#111827]">{responsible}</span>
        </div>
      </div>

      <div className="flex items-start gap-[0.625rem]">
        <span className="shrink-0"><UsersIcon /></span>
        <div className="flex min-w-0 flex-1 flex-col gap-[0.312rem] text-[0.875rem] sm:text-[1rem]">
          <span className="text-[#9ca3af]">Комиссия</span>
          <div className="flex flex-wrap gap-[0.312rem]">
            {commission.map((name, i) => (
              <span
                key={i}
                className="break-words rounded-[50px] bg-[#f3f4f6] px-[1rem] py-[0.375rem] text-[0.812rem] text-[#475569] sm:px-[1.5rem] sm:py-[0.5rem] sm:text-[1rem]"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-auto flex items-center gap-[0.625rem] text-[0.875rem] text-[#9ca3af] sm:text-[1rem]">
        <span className="shrink-0"><TrophyIcon /></span>
        {hasResults ? 'Результаты' : 'Результаты ещё не добавлены'}
      </div>
    </article>
  )
}

export default EventCard
